import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { get, isEmpty, isEqual } from 'lodash';
import classNames from 'classnames';
import qs from 'query-string';

import {
  createGuestFeedbackAction,
  updateGuestFeedbackAction,
  fetchDealByIdAction,
  subscribeDealAction,
  getBrandFeedbackSetupAction,
  fetchSubscribedGuestDealAction,
} from '../../../actions/app.action';
import asyncRoute from '../../../helpers/hoc/asyncRoute';
import { apiResponseCodes, defaulRewardCouponExpirationInDays } from '../../../constants/globals';
import routes from '../../../routes';

import HappyEmoji from '../../../assets/images/icons/happy-emoji.svg';
import LoveEmoji from '../../../assets/images/icons/love-emoji.svg';
import SadEmoji from '../../../assets/images/icons/sad-emoji.svg';
import './FeedbackForm.scss';

const TextArea = asyncRoute(() => import('../../../components/elements/TextAreaComponent'));
const Button = asyncRoute(() => import('../../../components/elements/Button'));

const FeedbackRewardPopup = asyncRoute(() =>
  import('../../../components/Popup/FeedbackRewardPopup'),
);
const SelectOutletPopup = asyncRoute(() => import('../../../components/Popup/SelectOutletPopup'));
const FeedbackGuestPopup = asyncRoute(() => import('../../../components/Popup/FeedbackGuestPopup'));
const Popup = asyncRoute(() => import('../../../components/Popup'));

const answerOptions = [
  { icon: SadEmoji, rating: 'RATING_1', title: 'Not Happy' },
  { icon: HappyEmoji, rating: 'RATING_3', title: 'Its Fine' },
  { icon: LoveEmoji, rating: 'RATING_5', title: 'Loved it' },
];

const FeedbackForm = ({
  match,
  history,
  readOnlyOutletDetails,
  guestFeedback,
  guestData,
  feedbackSetup,
  brandDeal,
  appStartupInfo,
  actions,
  location,
  brandGuestId,
}) => {
  const [rating, setRating] = useState('');
  const [remark, setRemark] = useState('');
  const [restaurant, setOutlet] = useState({});
  const [openSelectOutletPopup, setOpenSelectOutletPopup] = useState(false);
  const [openFeedbackRewardPopup, setOpenFeedbackRewardPopup] = useState(false);
  const [openFeedbackGuestPopup, setOpenFeedbackGuestPopup] = useState(false);
  const [isFeedbackSetup, setIsFeedbackSetup] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [feedbackDiscardedFrom, setFeedbackDiscardedFrom] = useState('');
  const [isDealExpired, setIsDealExpired] = useState(false);

  useEffect(() => {
    const { brandId } = appStartupInfo;
    actions.getBrandFeedbackSetup({ brandId }).then((response) => {
      if (get(response, 'data.status', false)) {
        if (readOnlyOutletDetails.length === 1) {
          //For one outlet in brand, set that outlet to receive feedback

          const draftRestaurant = readOnlyOutletDetails.find((restaurant) => restaurant) || {};
          history.push(`/${routes.containers.feedback}/${get(draftRestaurant, 'restaurantCode')}`);
          setOutlet(draftRestaurant);
        } else if (readOnlyOutletDetails.length > 1) {
          //For more than one outlet in brand, show select outlet popup or use param to receive feedback

          const draftRestaurant = readOnlyOutletDetails.find((restaurant) =>
            isEqual(get(restaurant, 'restaurantCode'), get(match, 'params.restaurantCode', '')),
          );
          !isEmpty(draftRestaurant) ? setOutlet(draftRestaurant) : setOpenSelectOutletPopup(true);
        }
      } else {
        setIsFeedbackSetup(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(rating)) {
      setErrorMessage('');
    }
  }, [rating]);

  const handleOutletSelectCTA = (restaurant) => {
    setOpenSelectOutletPopup(false);
    setOutlet(restaurant);
    history.push(`/${routes.containers.feedback}/${get(restaurant, 'restaurantCode')}?change=1`);
  };

  const handleOpenGetCouponCodePopup = () => {
    setOpenFeedbackRewardPopup(false);
    setOpenFeedbackGuestPopup(true);
  };

  const handleCloseFeedbackRewardPopup = () => {
    setOpenFeedbackRewardPopup(false);
    setFeedbackDiscardedFrom('FRP');
  };

  const handleCloseFeedbackGuestPopup = () => {
    setOpenFeedbackGuestPopup(false);
    setFeedbackDiscardedFrom('FGP');
  };

  const handleFeedbackStay = () => {
    switch (feedbackDiscardedFrom) {
      case 'FGP':
        setFeedbackDiscardedFrom('');
        setTimeout(() => setOpenFeedbackGuestPopup(true), 400);
        return;
      case 'FRP':
        setFeedbackDiscardedFrom('');
        setTimeout(() => setOpenFeedbackRewardPopup(true), 400);
        return;
      default:
        return;
    }
  };

  const handleFeedbackDiscard = () => {
    setRemark('');
    setRating('');
    setFeedbackDiscardedFrom('');
  };

  const handleOpenFeedbackRewardPopup = () => {
    actions
      .fetchDealById({ dealCode: get(feedbackSetup, 'rewardId'), guestId: get(guestData, 'id') })
      .then((responseObj) => {
        if (apiResponseCodes.SUCCESS.includes(get(responseObj, 'status'))) {
          if (get(responseObj, 'data.isExpired')) {
            setOpenFeedbackGuestPopup(true);
            setIsDealExpired(true);
          } else {
            setOpenFeedbackRewardPopup(true);
            setIsDealExpired(false);
          }
        }
      });
  };

  const handleFeedbackGuestProceed = (guestDetails) => {
    const guestId = get(guestDetails, 'id', '');

    if (get(feedbackSetup, 'hasReward') && !isDealExpired) {
      const { id: dealId, guestCoupons } = brandDeal;
      const guestCoupon = (guestCoupons && guestCoupons.find((gCoupon) => gCoupon)) || {};
      const availedCouponDetails = get(guestCoupon, 'activeDealGuests.0', {});
      const couponExpireTime = get(availedCouponDetails, 'expireTime', '');
      const currentDate = moment().format();
      const couponExpirationInDays =
        couponExpireTime && moment(couponExpireTime).diff(currentDate, 'days');

      // checked if reward coupon is already issued to guest or coupon expiration <= 15 days
      if (
        isEmpty(availedCouponDetails) ||
        couponExpirationInDays <= defaulRewardCouponExpirationInDays
      ) {
        const subscribeDealReqBody = {
          dealId,
          dealGuest: {
            guestId,
            brandGuestId,
          },
        };
        actions.subscribeDeal({ subscribeDealReqBody }).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            const guestFeedbackReqBody = {
              guestId,
              couponId: get(response, 'data.dealGuest.id', ''),
            };
            handleUpdateFeedback(guestFeedbackReqBody);
          }
        });
      } else {
        actions
          .fetchSubscribedGuestDeal({ couponCode: get(availedCouponDetails, 'couponCode') })
          .then((response) => {
            if (response) {
              handleUpdateFeedback({ guestId });
            }
          });
      }
    } else {
      handleUpdateFeedback({ guestId });
    }
  };

  const handleCreateFeedback = () => {
    if (isEmpty(rating)) {
      setErrorMessage('Please select an option to submit feedback');
      return;
    }
    const requestBody = {
      brandId: get(feedbackSetup, 'brandId'),
      outletId: get(restaurant, 'id', ''),
      rating,
      remark,
    };

    actions.createGuestFeedback(requestBody).then((responseObj) => {
      if (apiResponseCodes.SUCCESS.includes(get(responseObj, 'status'))) {
        get(feedbackSetup, 'hasReward')
          ? handleOpenFeedbackRewardPopup()
          : setOpenFeedbackGuestPopup(true);
      }
    });
  };

  const handleUpdateFeedback = (guestFeedbackReqBody) => {
    const requestBody = {
      guestFeedbackReqBody,
      guestFeedbackId: get(guestFeedback, 'id', ''),
    };
    actions.updateGuestFeedback(requestBody).then((responseObj) => {
      if (apiResponseCodes.SUCCESS.includes(get(responseObj, 'status'))) {
        history.push(
          `/${routes.containers.feedback}/${get(restaurant, 'restaurantCode')}/${
            routes.feedback.success
          }`,
        );
      }
    });
  };

  const renderAnswerOptions = (answerOptions) =>
    answerOptions &&
    answerOptions.length > 0 &&
    answerOptions.map((answerOption, index) => (
      <div key={index} className="option">
        <img
          className={classNames('emoji', {
            active: isEqual(get(answerOption, 'rating', ''), rating),
          })}
          src={get(answerOption, 'icon', '')}
          onClick={() => setRating(get(answerOption, 'rating', ''))}
          alt="emoji"
        />
        <span
          className={classNames('rating', {
            active: isEqual(get(answerOption, 'rating', ''), rating),
          })}
        >
          {get(answerOption, 'title', '')}
        </span>
      </div>
    ));

  const parsedQueryParams = qs.parse(get(location, 'search', {}));

  return (
    <>
      {isEqual(get(appStartupInfo, 'brand.status', ''), 'DEMO') && (
        <div className="feedback-form_banner">{'Demo Mode'}</div>
      )}
      <div className="feedback-form_wrapper">
        <div className="feedback-form">
          <div className="feedback-form_fields">
            <div className="feedback-form_brand">
              <img src={get(appStartupInfo, 'brand.logoUrl')} alt="logo" />
            </div>
            <div className="feedback-form_outlet">
              <span className="restaurant-name">
                {get(restaurant, 'name', get(appStartupInfo, 'title', ''))}
              </span>
              {isEqual(get(parsedQueryParams, 'change', ''), '1') &&
                readOnlyOutletDetails &&
                readOnlyOutletDetails.length > 1 &&
                isFeedbackSetup &&
                !isEmpty(restaurant) && (
                  <span className="actionBtn" onClick={() => setOpenSelectOutletPopup(true)}>
                    Change
                  </span>
                )}
            </div>
            {isFeedbackSetup ? (
              <>
                <div className="feedback-form_section">
                  {!isEmpty(errorMessage) && (
                    <div className="feedback-form_error">{errorMessage}</div>
                  )}
                  <div className="question">How was your experience?</div>
                  <div className="answer">{renderAnswerOptions(answerOptions)}</div>
                </div>
                <TextArea
                  rows={5}
                  name="remark"
                  value={remark}
                  placeholder="Tell us more (optional)"
                  maxLength={250}
                  showCharCount={true}
                  onChange={(event) => setRemark(event.target.value || '')}
                />
              </>
            ) : (
              <div className="feedback-form_exception">
                <span className="title">
                  {`Sorry, we’re not taking 
                online Feedback at the moment`}
                </span>
                <span className="description">
                  {` Please reach out to our staff 
                for any further assistance`}
                </span>
              </div>
            )}
          </div>
          {isFeedbackSetup && <Button content={'SUBMIT FEEDBACK'} onClick={handleCreateFeedback} />}
        </div>
        {openFeedbackRewardPopup && (
          <FeedbackRewardPopup
            openPopup={openFeedbackRewardPopup}
            onDismissSegmentPopup={handleCloseFeedbackRewardPopup}
            feedback={rating}
            brandDeal={brandDeal}
            handleCTA={handleOpenGetCouponCodePopup}
          />
        )}
        {readOnlyOutletDetails && readOnlyOutletDetails.length > 1 && openSelectOutletPopup && (
          <SelectOutletPopup
            openPopup={openSelectOutletPopup}
            outletList={readOnlyOutletDetails}
            onOutletSelect={handleOutletSelectCTA}
            {...(!isEmpty(restaurant) && {
              onDismissSegmentPopup: () => setOpenSelectOutletPopup(false),
            })}
          />
        )}

        <FeedbackGuestPopup
          open={openFeedbackGuestPopup}
          onClose={handleCloseFeedbackGuestPopup}
          hasReward={get(feedbackSetup, 'hasReward') && !isDealExpired}
          brandId={get(feedbackSetup, 'brandId')}
          guestData={guestData}
          handleProceed={handleFeedbackGuestProceed}
        />

        <Popup
          className="feedback-discard-popup"
          openSegmentPopup={!isEmpty(feedbackDiscardedFrom)}
          content={{
            title: 'Discard Feedback',
            description: 'Are you sure you do not want to submit your feedback?',
            popupCTA: 'STAY HERE',
            secondaryCTA: 'DISCARD',
          }}
          handleCTA={handleFeedbackStay}
          handleSecondaryCTA={handleFeedbackDiscard}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  readOnlyOutletDetails: get(state, 'common.outletDetails._embedded.restaurants', []),
  guestFeedback: get(state, 'app.guestFeedback', {}),
  guestData: get(state, 'authentication.guestData', {}),
  feedbackSetup: get(state, 'app.feedbackSetup', {}),
  brandDeal: get(state, 'app.brandDeal', {}),
  brandGuestId: get(state, 'rewards.rewardGuestDetails.id', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createGuestFeedback: createGuestFeedbackAction,
      updateGuestFeedback: updateGuestFeedbackAction,
      fetchDealById: fetchDealByIdAction,
      subscribeDeal: subscribeDealAction,
      getBrandFeedbackSetup: getBrandFeedbackSetupAction,
      fetchSubscribedGuestDeal: fetchSubscribedGuestDealAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackForm);
